import React, { useState } from 'react';
import { Button, Form, Row, Col, Modal, InputGroup, Tabs, Tab } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import moment from 'moment';
import { KIEMTRASOLIEU_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { Put } from '../../../../utils/httpProvider';
import { trangThaiKiemTraSoLieuValue, trangThaiGuiKetQuaValue } from '../../../../utils/define';
import DocumentInput from '../../../../components/document-input/DocumentInput';

const schema = yup.object().shape({
    soLuongBaiBao: yup.number().nullable().typeError('Vui lòng nhập số').min(0, 'Số lượng bài báo không được âm').required('Vui lòng nhập số lượng bài báo'),
    trenCacTapChi: yup.string().required('Vui lòng nhập tên các tạp chí'),
    linkBaiBao: yup.string().nullable().test(
        'is-link-or-file-required',
        'Vui lòng nhập Link bài báo hoặc Gắn file',
        function (value) {
            const { fileBaiBao } = this.parent;
            if (!value && !fileBaiBao) {
                return false;
            }
            return true;
        }
    ),
    ngayKiemTra: yup.date().nullable(),
    fileVanBanDongY: yup.string().required('Vui lòng gắn file Văn bản đồng ý'),
    fileLuanAn: yup.string().required('Vui lòng gắn file Luận án')
});

const FieldDisplayFile = ({ value }) => {
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
        const safePath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${safePath}`;
    };

    let files = [];
    if (value) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                files = parsed;
            } else {
                files = [{ filePath: value, name: value.split('/').pop() }];
            }
        } catch (e) {
            files = [{ filePath: value, name: value.split('/').pop() }];
        }
    }

    return (
        <div className="d-flex flex-wrap">
            {files.length > 0 ? (
                files.map((file, idx) => {
                    const path = file.filePath || file;
                    let name = file.name || (typeof path === 'string' ? path.split('/').pop() : 'File');
                    name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                    return (
                        <a key={idx} href={getSafeUrl(path)} target="_blank" rel="noreferrer" className="btn btn-outline-primary mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                            <i className="fas fa-paperclip mr-1"></i> {name}
                        </a>
                    );
                })
            ) : (
                <span className="text-muted small">Không có file đính kèm</span>
            )}
        </div>
    );
};

const KiemTraSoLieuEdit = ({ hoSoId, dataEdit, setViewMode, refreshList, isView, isUserView = false }) => {
    const queryClient = useQueryClient();

    const editFunc = useMutation((value) => Put(KIEMTRASOLIEU_API.UPDATE_ITEM, dataEdit.id, value));

    const handleUpdate = (values, { setSubmitting }) => {
        const requestData = {
            id: dataEdit.id,
            hoSoNghienCuuSinhId: hoSoId,
            soLuongBaiBao: values.soLuongBaiBao ? Number(values.soLuongBaiBao) : null,
            trenCacTapChi: values.trenCacTapChi || '',
            linkBaiBao: values.linkBaiBao || '',
            ngayKiemTra: values.ngayKiemTra || null,
            fileBaiBao: values.fileBaiBao || '',
            fileVanBanDongY: values.fileVanBanDongY || '',
            fileDanhSach: values.fileDanhSach || '',
            fileXacNhan: values.fileXacNhan || '',
            fileLuanAn: values.fileLuanAn || '',
            fileMinhChung: values.fileMinhChung || '',
            ghiChuChinhSua: dataEdit.ghiChuChinhSua || '',
            trangThai: dataEdit.trangThai ?? trangThaiKiemTraSoLieuValue.choDuyet
        };

        editFunc.mutate(requestData, {
            onSuccess: (res) => {
                setSubmitting(false);
                if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id)) {
                    toast.success('Cập nhật thành công');
                    queryClient.invalidateQueries(['kiemTraSoLieuData', hoSoId]);
                    if (refreshList) refreshList();
                    setViewMode('list');
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra');
                }
            },
            onError: (err) => {
                setSubmitting(false);
                toast.error(err?.message || 'Có lỗi xảy ra trong quá trình xử lý');
            }
        });
    };

    return (
        <Modal show={true} onHide={() => setViewMode('list')} size="xl" backdrop="static">
            <Modal.Header closeButton className="bg-primary text-white">
                <Modal.Title>{isView ? 'Chi tiết Kiểm tra số liệu' : 'Cập nhật Kiểm tra số liệu'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 custom-modal-body" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', overflowX: 'hidden' }}>
                <div className="bg-light p-3 rounded border m-3">
                        {dataEdit.ghiChuChinhSua && (
                    <div className="alert alert-warning mb-3">
                        <strong>Ghi chú từ HLĐT:</strong> {dataEdit.ghiChuChinhSua}
                    </div>
                )}
                <Formik
                    validationSchema={schema}
                    onSubmit={handleUpdate}
                    initialValues={{
                        soLuongBaiBao: dataEdit.soLuongBaiBao || '',
                        trenCacTapChi: dataEdit.trenCacTapChi || '',
                        linkBaiBao: dataEdit.linkBaiBao || '',
                        ngayKiemTra: dataEdit.ngayKiemTra ? moment(dataEdit.ngayKiemTra).format('YYYY-MM-DD') : '',
                        fileBaiBao: dataEdit.fileBaiBao || '',
                        fileVanBanDongY: dataEdit.fileVanBanDongY || '',
                        fileDanhSach: dataEdit.fileDanhSach || '',
                        fileXacNhan: dataEdit.fileXacNhan || '',
                        fileLuanAn: dataEdit.fileLuanAn || '',
                        fileMinhChung: dataEdit.fileMinhChung || ''
                    }}
                >
                    {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting, setFieldValue, submitCount }) => (
                        <Form noValidate onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle">
                                    <thead className="bg-light text-center">
                                        <tr>
                                            <th width="60" className="text-center font-weight-bold">TT</th>
                                            <th width="45%" className="text-center font-weight-bold">Điều kiện</th>
                                            <th className="text-center font-weight-bold">Hành động</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }} rowSpan={5}>1</td>
                                            <td style={{ fontWeight: 500 }}>Bài báo khoa học</td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td>Số lượng bài báo <span className="text-danger">*</span></td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    name="soLuongBaiBao"
                                                    value={values.soLuongBaiBao}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Nhập số lượng bài báo..."
                                                    min="0"
                                                    disabled={isView}
                                                    className={isView ? "bg-white text-dark shadow-none" : ""}
                                                />
                                                {submitCount > 0 && errors.soLuongBaiBao && <div className="text-danger small mt-1">{errors.soLuongBaiBao}</div>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Trên các tạp chí <span className="text-danger">*</span></td>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    name="trenCacTapChi"
                                                    value={values.trenCacTapChi}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Nhập tên tạp chí..."
                                                    disabled={isView}
                                                    className={isView ? "bg-white text-dark shadow-none" : ""}
                                                />
                                                {submitCount > 0 && errors.trenCacTapChi && <div className="text-danger small mt-1">{errors.trenCacTapChi}</div>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Link bài báo <span className="text-danger">*</span></td>
                                            <td>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        name="linkBaiBao"
                                                        value={values.linkBaiBao}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Nhập link bài báo..."
                                                        disabled={isView}
                                                        className={isView ? "bg-white text-dark shadow-none" : ""}
                                                    />
                                                    {values.linkBaiBao && (
                                                        <InputGroup.Append>
                                                            <a 
                                                                href={values.linkBaiBao.startsWith('http') ? values.linkBaiBao : `http://${values.linkBaiBao}`} 
                                                                target="_blank" 
                                                                rel="noreferrer" 
                                                                className="btn btn-outline-primary"
                                                                title="Mở link bài báo"
                                                            >
                                                                <i className="fas fa-external-link-alt"></i>
                                                            </a>
                                                        </InputGroup.Append>
                                                    )}
                                                </InputGroup>
                                                {submitCount > 0 && errors.linkBaiBao && <div className="text-danger small mt-1">{errors.linkBaiBao}</div>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Gắn file (nếu không có đường link)</td>
                                            <td>
                                                {isView ? (
                                                    <FieldDisplayFile value={values.fileBaiBao} />
                                                ) : (
                                                    <DocumentInput
                                                        labelTitle=""
                                                        value={values.fileBaiBao}
                                                        onChangeProp={(url) => setFieldValue('fileBaiBao', url)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }}>2</td>
                                            <td style={{ fontWeight: 500 }}>Văn bản đồng ý của các đồng tác giả <span className="text-danger">*</span></td>
                                            <td>
                                                {isView ? (
                                                    <FieldDisplayFile value={values.fileVanBanDongY} />
                                                ) : (
                                                    <DocumentInput
                                                        labelTitle=""
                                                        value={values.fileVanBanDongY}
                                                        onChangeProp={(url) => setFieldValue('fileVanBanDongY', url)}
                                                    />
                                                )}
                                                {submitCount > 0 && errors.fileVanBanDongY && <div className="text-danger small mt-1">{errors.fileVanBanDongY}</div>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }}>3</td>
                                            <td style={{ fontWeight: 500 }}>Danh sách bệnh nhân</td>
                                            <td>
                                                {isView ? (
                                                    <FieldDisplayFile value={values.fileDanhSach} />
                                                ) : (
                                                    <DocumentInput
                                                        labelTitle=""
                                                        value={values.fileDanhSach}
                                                        onChangeProp={(url) => setFieldValue('fileDanhSach', url)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }}>4</td>
                                            <td style={{ fontWeight: 500 }}>Xác nhận đơn vị xử lý số liệu</td>
                                            <td>
                                                {isView ? (
                                                    <FieldDisplayFile value={values.fileXacNhan} />
                                                ) : (
                                                    <DocumentInput
                                                        labelTitle=""
                                                        value={values.fileXacNhan}
                                                        onChangeProp={(url) => setFieldValue('fileXacNhan', url)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }}>5</td>
                                            <td style={{ fontWeight: 500 }}>Luận án đã thông qua bộ môn <span className="text-danger">*</span></td>
                                            <td>
                                                {isView ? (
                                                    <FieldDisplayFile value={values.fileLuanAn} />
                                                ) : (
                                                    <DocumentInput
                                                        labelTitle=""
                                                        value={values.fileLuanAn}
                                                        onChangeProp={(url) => setFieldValue('fileLuanAn', url)}
                                                    />
                                                )}
                                                {submitCount > 0 && errors.fileLuanAn && <div className="text-danger small mt-1">{errors.fileLuanAn}</div>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center" style={{ fontWeight: 500 }}>6</td>
                                            <td style={{ fontWeight: 500 }}>Thời gian dự kiến kiểm tra</td>
                                            <td>
                                                <Form.Control
                                                    type="date"
                                                    name="ngayKiemTra"
                                                    value={values.ngayKiemTra}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={isView}
                                                    className={isView ? "bg-white text-dark shadow-none" : ""}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-right mt-3">
                                {!isView && (
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <><i className="fas fa-spinner fa-spin mr-1"></i> Cập nhật</> : 'Cập nhật'}
                                    </Button>
                                )}
                            </div>
                        </Form>
                    )}
                </Formik>
                    </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setViewMode('list')}>
                    Đóng
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default KiemTraSoLieuEdit;
